import Link from 'next/link';
import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

export type VarianteEnlaceCta = 'primario' | 'inverso' | 'secundario';
export type AltoEnlaceCta = 44 | 46 | 52 | 56;

interface PropiedadesEnlaceCta {
    href: string;
    variante: VarianteEnlaceCta;
    alto?: AltoEnlaceCta;
    flecha?: boolean;
    anchoCompleto?: boolean;
    className?: string;
    children: ReactNode;
}

/**
 * Los CTA del lienzo 2.0 no reutilizan `ButtonLink`: el lienzo los dibuja con radio 12 e
 * Inter, y el primitivo del producto lleva radio 16 y Outfit. Solo se copia la receta de
 * hover/active de ESTILOS_BOTON, que el lienzo no dibuja; con movimiento reducido el
 * desplazamiento desaparece y queda solo el cambio de sombra.
 */
const BASE =
    'inline-flex items-center justify-center gap-ds-2 border-2 border-ink font-sans no-underline ' +
    'transition-all duration-ds-base hover:-translate-y-0.5 active:translate-y-0.5 active:shadow-none ' +
    'motion-reduce:hover:translate-y-0 motion-reduce:active:translate-y-0';

/** En reposo solo el primario lleva sombra; al pasar el ratón todos suben un paso. */
const VARIANTES: Record<VarianteEnlaceCta, string> = {
    primario: 'bg-accent-blue text-on-accent-blue font-bold',
    inverso: 'bg-ink text-canvas font-bold hover:shadow-ds-2',
    secundario: 'bg-surface-2 text-ink font-semibold hover:shadow-ds-2',
};

/**
 * Las alturas del lienzo traen su radio, relleno y cuerpo: 52/56 son los CTA de sección y
 * de banda (radio 12); 44/46 son los CTA en línea dentro de un texto (radio 10, sin sombra).
 */
const ALTOS: Record<AltoEnlaceCta, string> = {
    44: 'h-11 rounded-[10px] px-ds-4 text-[15px]',
    46: 'h-[46px] rounded-[10px] px-ds-4 text-[15px]',
    52: 'h-[52px] rounded-ds-md px-5 text-ds-400',
    56: 'h-14 rounded-ds-md px-ds-6 text-[17px]',
};

function sombraPrimario(alto: AltoEnlaceCta): string {
    return alto >= 52 ? 'shadow-ds-2 hover:shadow-ds-3' : 'hover:shadow-ds-2';
}

function Flecha() {
    return (
        <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className="shrink-0"
        >
            <path d="M3 8h10M9 4l4 4-4 4" />
        </svg>
    );
}

export default function EnlaceCta({
    href,
    variante,
    alto = 52,
    flecha = false,
    anchoCompleto = false,
    className,
    children,
}: PropiedadesEnlaceCta) {
    const clases = cn(
        BASE,
        ALTOS[alto],
        VARIANTES[variante],
        variante === 'primario' && sombraPrimario(alto),
        anchoCompleto && 'w-full',
        className,
    );
    return (
        <Link href={href} className={clases}>
            {children}
            {flecha && <Flecha />}
        </Link>
    );
}
